import React, { useState, useEffect, useRef } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { useClient } from '../contexts/ClientContext';
import { sendClientData } from '../services/emailService';
import { 
    GiftIcon, 
    CrownIcon, 
    StarIcon, 
    XIcon, 
    MinusIcon, 
    SparklesIcon, 
    ShieldIcon,
    PhoneIcon,
    CheckCircleIcon
} from './Icons';

interface PromoNotificationWidgetProps {
    onOpenPremium?: (pack?: '50' | '100') => void;
    onOpenSarah?: () => void;
    onOpenTarot?: () => void;
}

export const PromoNotificationWidget: React.FC<PromoNotificationWidgetProps> = ({
    onOpenPremium,
    onOpenSarah,
    onOpenTarot
}) => {
    const { promoConfig, logSubmission } = useAdmin();
    const { client } = useClient();

    const [isVisible, setIsVisible] = useState(false);
    const [isMinimized, setIsMinimized] = useState(true);
    const [isDismissed, setIsDismissed] = useState(false);

    // Form states for 'open_form' mode
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Target Audience Filtering
    const isTargetMatched = () => {
        if (!promoConfig || !promoConfig.enabled) return false;
        const hasAccount = Boolean(client && client.id);

        if (promoConfig.targetAudience === 'guests_only' && hasAccount) return false;
        if (promoConfig.targetAudience === 'registered_only' && !hasAccount) return false;
        return true;
    };

    // State cookie consent pour éviter la superposition de popups au chargement
    const [hasCookieConsent, setHasCookieConsent] = useState(() => {
        if (typeof window === 'undefined') return true;
        return Boolean(localStorage.getItem('astro_cookie_consent'));
    });

    useEffect(() => {
        const handleConsentUpdate = () => {
            setHasCookieConsent(Boolean(localStorage.getItem('astro_cookie_consent')));
        };
        window.addEventListener('astro_cookie_consent_updated', handleConsentUpdate);
        window.addEventListener('storage', handleConsentUpdate);
        return () => {
            window.removeEventListener('astro_cookie_consent_updated', handleConsentUpdate);
            window.removeEventListener('storage', handleConsentUpdate);
        };
    }, []);

    // Reset dismissed state whenever promoConfig updates from admin
    useEffect(() => {
        if (promoConfig?.enabled) {
            setIsDismissed(false);
        }
    }, [promoConfig?.title, promoConfig?.enabled, promoConfig?.template]);

    useEffect(() => {
        // Attendre impérativement que les cookies soient traités avant d'afficher la moindre promo
        if (!hasCookieConsent) {
            setIsVisible(false);
            return;
        }

        if (!promoConfig || !promoConfig.enabled || isDismissed || !isTargetMatched()) {
            setIsVisible(false);
            return;
        }

        setIsMinimized(promoConfig.startMinimized ?? false);

        const delayMs = Math.max(1, promoConfig.displayDelaySeconds || 4) * 1000;

        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            if (promoConfig?.enabled && isTargetMatched() && !isDismissed && hasCookieConsent) {
                setIsVisible(true);
            }
        }, delayMs);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [promoConfig, client, isDismissed, hasCookieConsent]);

    if (!isVisible || isDismissed || !isTargetMatched() || !hasCookieConsent) {
        return null;
    }

    // --- POSITION CLASSES ---
    const getPositionClass = () => {
        switch (promoConfig.position) {
            case 'bottom-right':
                return 'bottom-20 md:bottom-28 right-2 md:right-4';
            case 'top-left':
                return 'top-20 md:top-24 left-2 md:left-4';
            case 'top-right':
                return 'top-20 md:top-24 right-2 md:right-4';
            case 'bottom-center':
                return 'bottom-20 md:bottom-28 left-1/2 -translate-x-1/2';
            case 'bottom-left':
            default:
                return 'bottom-20 md:bottom-28 left-2 md:left-4';
        }
    };

    // --- SIZE CLASSES ---
    const getSizeClass = () => {
        switch (promoConfig.size) {
            case 'compact':
                return 'w-[280px] sm:w-[310px]';
            case 'large':
                return 'w-[350px] sm:w-[420px]';
            case 'normal':
            default:
                return 'w-[315px] sm:w-[370px]';
        }
    };

    // --- THEME COLOR STYLES ---
    const getThemeConfig = () => {
        switch (promoConfig.gradientTheme) {
            case 'purple':
                return {
                    border: 'border-purple-500/80',
                    shadow: 'shadow-[0_0_35px_rgba(168,85,247,0.45)]',
                    accentGrad: 'from-purple-500 to-indigo-600',
                    badgeBg: 'bg-purple-500 text-white',
                    btnGrad: 'from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white',
                    iconColor: 'text-purple-300',
                    highlightBorder: 'border-purple-500/40 bg-purple-900/20'
                };
            case 'emerald':
                return {
                    border: 'border-emerald-500/80',
                    shadow: 'shadow-[0_0_35px_rgba(16,185,129,0.45)]',
                    accentGrad: 'from-emerald-500 to-teal-600',
                    badgeBg: 'bg-emerald-500 text-black font-extrabold',
                    btnGrad: 'from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-black font-bold',
                    iconColor: 'text-emerald-300',
                    highlightBorder: 'border-emerald-500/40 bg-emerald-900/20'
                };
            case 'crimson':
                return {
                    border: 'border-rose-500/80',
                    shadow: 'shadow-[0_0_35px_rgba(244,63,94,0.45)]',
                    accentGrad: 'from-rose-500 to-pink-600',
                    badgeBg: 'bg-rose-500 text-white font-bold',
                    btnGrad: 'from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white',
                    iconColor: 'text-rose-300',
                    highlightBorder: 'border-rose-500/40 bg-rose-900/20'
                };
            case 'night':
                return {
                    border: 'border-indigo-500/80',
                    shadow: 'shadow-[0_0_35px_rgba(99,102,241,0.45)]',
                    accentGrad: 'from-indigo-500 to-blue-600',
                    badgeBg: 'bg-indigo-500 text-white font-bold',
                    btnGrad: 'from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white',
                    iconColor: 'text-indigo-300',
                    highlightBorder: 'border-indigo-500/40 bg-indigo-900/20'
                };
            case 'gold':
            default:
                return {
                    border: 'border-gold-500/80',
                    shadow: 'shadow-[0_0_35px_rgba(234,179,8,0.45)]',
                    accentGrad: 'from-gold-400 to-yellow-600',
                    badgeBg: 'bg-gold-500 text-black font-extrabold',
                    btnGrad: 'from-gold-600 via-yellow-500 to-gold-600 hover:from-gold-500 hover:to-yellow-400 text-black font-extrabold',
                    iconColor: 'text-gold-300',
                    highlightBorder: 'border-gold-500/40 bg-gold-900/20'
                };
        }
    };

    const theme = getThemeConfig();

    const handleActionClick = () => {
        if (promoConfig.ctaAction === 'open_premium') {
            if (onOpenPremium) onOpenPremium('50');
            setIsMinimized(true);
        } else if (promoConfig.ctaAction === 'open_sarah') {
            if (onOpenSarah) onOpenSarah();
            setIsMinimized(true);
        } else if (promoConfig.ctaAction === 'open_tarot') {
            if (onOpenTarot) onOpenTarot();
            window.location.hash = '#tarot';
            setIsMinimized(true);
        } else if (promoConfig.ctaAction === 'open_form') {
            setIsMinimized(false);
        }
    };

    const handleSubmitLead = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!name.trim() || name.length < 2) {
            setError("Veuillez entrer un prénom valide.");
            return;
        }

        const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-79](?:[\s.-]*\d{2}){4}$/;
        if (!phoneRegex.test(phone)) {
            setError("Numéro invalide. Uniquement France (06/07/01-05/09).");
            return;
        }

        setLoading(true);

        logSubmission({
            type: "Promo " + (promoConfig.badgeText || "Offre"),
            name: name,
            phone: phone,
            details: `Email: ${email || 'N/A'}. Offre: ${promoConfig.title}`
        });

        await sendClientData({
            name: name,
            phone: phone,
            subject: `PROMO: ${promoConfig.title}`,
            details: `Email: ${email || 'N/A'}. Le client réclame: ${promoConfig.giftHighlight || promoConfig.badgeText}`
        });

        setLoading(false);
        setIsSubmitted(true);

        setTimeout(() => {
            setIsSubmitted(false);
            setIsMinimized(true);
        }, 4000);
    };

    // --- MODE BULLE FLOTTANTE (MINIMISÉE) ---
    if (isMinimized) {
        return (
            <div 
                className={`fixed z-[85] flex items-center gap-2 animate-fade-in pointer-events-auto ${getPositionClass()}`} 
                style={{ touchAction: 'pan-y' }}
            >
                <button 
                    onClick={() => setIsMinimized(false)}
                    className={`group relative flex items-center gap-3 bg-[#0a0514]/95 border-2 ${theme.border} rounded-full pl-2.5 pr-4 py-2 ${theme.shadow} backdrop-blur-md hover:scale-105 transition-all text-left max-w-[90vw]`}
                >
                    <div className={`relative w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${theme.accentGrad} flex items-center justify-center text-black font-bold shadow-md shrink-0`}>
                        <GiftIcon className="w-5 h-5 text-black animate-bounce" />
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                        </span>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 truncate">
                            <span className="text-white font-bold text-xs leading-none truncate">{promoConfig.title}</span>
                            <span className={`${theme.badgeBg} text-[9px] px-1.5 py-0.5 rounded-full uppercase shrink-0`}>
                                {promoConfig.badgeText}
                            </span>
                        </div>
                        <p className="text-[10px] text-gray-300 mt-1 truncate max-w-[200px]">{promoConfig.subtitle}</p>
                    </div>
                </button>
                <button 
                    onClick={() => setIsDismissed(true)}
                    className="bg-black/80 hover:bg-red-900/80 text-gray-400 hover:text-white rounded-full p-2 border border-white/10 transition-colors shadow-lg shrink-0"
                    title="Fermer la promotion"
                >
                    <XIcon className="w-3.5 h-3.5" />
                </button>
            </div>
        );
    }

    // --- MODE CARTE COMPLÈTE (DÉPLIÉE) ---
    return (
        <div 
            className={`fixed z-[85] max-h-[85vh] overflow-y-auto animate-slide-up pointer-events-auto custom-scrollbar ${getPositionClass()} ${getSizeClass()}`} 
            style={{ touchAction: 'pan-y' }}
        >
            <div className={`relative bg-[#0d071a]/95 backdrop-blur-md rounded-2xl border-2 ${theme.border} ${theme.shadow} overflow-hidden shadow-2xl`}>
                
                {/* Texture de fond */}
                <div className="absolute inset-0 opacity-20 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>
                
                {/* Boutons Réduire & Fermer */}
                <div className="absolute top-2.5 right-2.5 z-30 flex items-center gap-1">
                    <button 
                        onClick={() => setIsMinimized(true)}
                        className="text-gray-400 hover:text-white bg-black/60 hover:bg-white/20 rounded-full p-1.5 transition-all border border-white/10"
                        title="Réduire en bulle"
                    >
                        <MinusIcon className="w-3.5 h-3.5" />
                    </button>
                    <button 
                        onClick={() => setIsDismissed(true)}
                        className="text-gray-400 hover:text-white bg-black/60 hover:bg-red-900/80 rounded-full p-1.5 transition-all border border-white/10"
                        title="Fermer l'offre"
                    >
                        <XIcon className="w-3.5 h-3.5" />
                    </button>
                </div>

                {/* Bandeau Supérieur avec Badge */}
                <div className={`w-full bg-gradient-to-r ${theme.accentGrad} py-1.5 px-3 flex items-center justify-between text-black relative z-10`}>
                    <span className="font-extrabold uppercase tracking-wider text-[10px] flex items-center gap-1">
                        <SparklesIcon className="w-3 h-3 text-black" /> {promoConfig.badgeText}
                    </span>
                    <span className="text-[9px] bg-black/30 text-white font-bold px-2 py-0.2 rounded-full">
                        Offre Exclusive
                    </span>
                </div>

                <div className="p-4 sm:p-5 relative z-10">
                    <div className="text-center mb-3">
                        <h3 className="text-lg sm:text-xl font-serif font-bold text-white mb-1">
                            {promoConfig.title}
                        </h3>
                        <p className="text-gray-300 text-xs leading-relaxed max-w-[300px] mx-auto">
                            {promoConfig.subtitle}
                        </p>
                    </div>

                    {/* Bloc Highlight Cadeau / Récompense */}
                    {promoConfig.giftHighlight && (
                        <div className={`mb-3 border ${theme.highlightBorder} rounded-xl p-2.5 flex items-center justify-center gap-2 text-center`}>
                            <GiftIcon className={`w-4 h-4 ${theme.iconColor} shrink-0`} />
                            <span className="text-xs font-extrabold text-white">
                                {promoConfig.giftHighlight}
                            </span>
                        </div>
                    )}

                    {/* Mode Formulaire (open_form) */}
                    {promoConfig.ctaAction === 'open_form' ? (
                        <div>
                            {!isSubmitted ? (
                                <form onSubmit={handleSubmitLead} className="space-y-2.5">
                                    <input 
                                        type="text" 
                                        required
                                        placeholder="Votre Prénom"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-gold-500 text-xs placeholder-gray-500"
                                    />
                                    <input 
                                        type="tel" 
                                        required
                                        placeholder="Numéro Mobile (06 / 07...)"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-gold-500 text-xs placeholder-gray-500"
                                    />
                                    <input 
                                        type="email" 
                                        placeholder="Votre E-mail (facultatif)"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-gold-500 text-xs placeholder-gray-500"
                                    />

                                    {error && (
                                        <p className="text-red-400 text-[10px] font-bold text-center">{error}</p>
                                    )}

                                    <button 
                                        type="submit" 
                                        disabled={loading}
                                        className={`w-full py-2.5 px-4 rounded-xl bg-gradient-to-r ${theme.btnGrad} text-xs uppercase tracking-wider shadow-lg transition-transform hover:scale-[1.02] flex items-center justify-center gap-1.5`}
                                    >
                                        {loading ? "Activation en cours..." : (promoConfig.ctaText || "Activer mon offre")}
                                    </button>
                                </form>
                            ) : (
                                <div className="bg-emerald-950/80 border border-emerald-500/50 rounded-xl p-3.5 text-center space-y-1">
                                    <CheckCircleIcon className="w-6 h-6 text-emerald-400 mx-auto" />
                                    <h4 className="text-white font-bold text-xs">Demande enregistrée !</h4>
                                    <p className="text-emerald-200 text-[10px]">Un conseiller va vous contacter sous peu.</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Mode Bouton Direct (open_premium / open_sarah / open_tarot) */
                        <button 
                            onClick={handleActionClick}
                            className={`w-full py-2.5 px-4 rounded-xl bg-gradient-to-r ${theme.btnGrad} text-xs uppercase tracking-wider shadow-lg transition-transform hover:scale-[1.02] flex items-center justify-center gap-1.5`}
                        >
                            <SparklesIcon className="w-3.5 h-3.5" />
                            <span>{promoConfig.ctaText || "Profiter de l'offre"}</span>
                        </button>
                    )}

                    <p className="text-[9px] text-gray-500 text-center mt-2.5">
                        Confidentialité garantie • Sans engagement
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PromoNotificationWidget;